import React, { useEffect, useMemo, useState } from 'react';
import { Container, Col, Row } from 'reactstrap';
import PageGroup from './PageGroups/PageGroup';
import { getFilteredQuestions } from 'helpers/sf_utils';
import { useHistory } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { useDispatch } from 'react-redux';
import { updateQuoteFunnelQuestionsWithAnswers } from 'store/quote_funnels/answers/actions';
import useQuery from 'hooks/useQuery';
import _ from 'lodash';
import { sort_by_order } from 'helpers/quote_funnel_helper';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';

const get_next_indexes = (state, answers, page_group_index, next_page_index, init_prefilled, review = 0) => {
  const current_page_group = state?.page_groups[page_group_index];
  const current_page_group_pages = current_page_group?.pages?.sort(sort_by_order);
  const current_page_group_questions = current_page_group_pages?.flatMap(page => page.question_groups?.sort(sort_by_order)?.flatMap(question_group => question_group?.questions));
  const next_page = current_page_group_pages?.[next_page_index];
  const is_last_page = state?.page_groups?.length - 1 === page_group_index && current_page_group_pages?.length - 1 === next_page_index;
  if (next_page_index < current_page_group_pages?.length) {
    // if next page has questions then only return the indexes
    const instance_prompt_question = current_page_group_questions.find(q => q.repeatable_type === 'add_another_instance_prompt');
    if (instance_prompt_question?.prefilled && instance_prompt_question.value === "No") {
      return get_next_indexes(state, answers, page_group_index, next_page_index + 1, init_prefilled);
    }

    // Filter questions based on depends_on
    let questions = _.flatten(next_page?.question_groups?.map(q_gr => getFilteredQuestions(q_gr, answers)));
    // filter questions based on init_prefilled (whether it was prefilled in SF)
    questions = questions.filter((q) => !init_prefilled.includes(q.field_name) && (q.required || q.skip || q.repeatable_type === 'add_another_instance_prompt'));
    // If not the last page, filter out any questions which are of 'add_another_instance_prompt' AND
    // all answers in that page group were init_prefilled
    if (!is_last_page) {
      questions = questions.filter(q => {
        if (q.repeatable_type !== 'add_another_instance_prompt') {
          return true;
        }

        const not_with_instance_prompt_question = current_page_group_questions.filter(cq => (cq.repeatable_type !== 'add_another_instance_prompt'))
        const are_all_questions_prefilled = not_with_instance_prompt_question.reduce((acc, curr) => acc && init_prefilled.includes(curr.field_name), true);
        if (!are_all_questions_prefilled && !q?.prefilled) {
          return true;
        }

        return false;
      })
    }

    if (!!questions?.length || review) {
      return {
        next_page_group_index: page_group_index,
        next_page_index: next_page_index
      }
    } else {
      return get_next_indexes(state, answers, page_group_index, next_page_index + 1, init_prefilled);
    }
  } else {
    // handle page group increment
    if (page_group_index + 1 < state?.page_groups?.length) {
      return get_next_indexes(state, answers, page_group_index + 1, 0, init_prefilled);
    }
  }

  return {
    next_page_group_index: page_group_index,
    next_page_index: next_page_index
  }
}

function QuoteFunnelAnswer({ quote_funnel_questions, quote_funnel_questions_loading, 
  session_id, quote_funnel_id, quote_form_visit, tcpa_disclaimer_text, meta
}) {
  const dispatch = useDispatch();
  const [isFeatureFlagActive] = useFeatureFlags();
  // const [page_group_current_index, setCurrentIndex] = useState(0);
  const query = useQuery();
  const page_group_current_index = parseInt(query.get('page_group_current_index') || 0);
  const review = parseInt(query.get('review') || 0);
  const preview = parseInt(query.get('preview') || 0);
  const history = useHistory();
  // Initially when this page is loaded, neither the page_group_current_index, not page_current_index is set
  // When the form is initally loaded, we may need to redirect to another page (first one which isn't prefilled)
  const [handledInitialPage, setHandledInitialPage] = useState(false);

  const handleCurrentPageGroup = () => {
    const current_page_group = initialCurrentState?.page_groups || [];
    const page_group_length = current_page_group?.length || 0;
    if (page_group_length > page_group_current_index + 1) {
      // setCurrentIndex(page_group_current_index + 1)
      return;
    }
    saveDetails(review ? 'edit' : 'submit');
    if (review !== 1) {
      history.push(`/forms/thank_you/session/${session_id}`)
    }
  }

  const [initialCurrentState, setInitialCurrentState] = useState(quote_funnel_questions);

  useEffect(() => {
    setInitialCurrentState(quote_funnel_questions)
  }, [quote_funnel_questions])

  const handleOnChangeEvent = (value, index) => {
    setInitialCurrentState(initial_current_state => {
      let new_initial_current_state = _.cloneDeep(initial_current_state);
      return ({
        ...new_initial_current_state,
        page_groups: new_initial_current_state?.page_groups?.map((page_group, idx) => {
          if (idx !== index) {
            return page_group;
          }

          return value;
        }).sort((a, b) => a?.order - b?.order)
      })
    })
  }

  const answers = initialCurrentState?.page_groups?.map(page_group => page_group?.pages?.map(page => page?.question_groups?.map(question_group => question_group?.questions?.map(question => ({ field_name: question?.field_name, prefilled: question?.prefilled, required: question?.required, skip: question?.skip, value: question?.value || "", skipped: question?.skipped }))))).flat(Infinity);
  const go_to_review_page = answers.reduce((acc, curr) => {
    return acc && (!(curr.required || curr.skip) || curr.prefilled || curr?.skipped)
  }, true)

  if ((parseInt(review || 0) < 1) && ((parseInt(initialCurrentState?.prefill_perc || 0) === 100) || (go_to_review_page))) {
    if (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_CLIENT_META_IN_QUOTE_FORMS)){
      history.push(`/forms/review/session/${session_id}?meta=${meta}`)
    } else {
      history.push(`/forms/review/session/${session_id}`)
    }
  }

  const saveDetails = (submit_type, page_group_current_index, page_current_index) => {
    const leadid_token = document.getElementById('leadid_token');
    const disclosureDiv = document.getElementById('leadid_tcpa_disclosure');
    const trustedFormCertUrl = document.getElementsByName('trustedFormCertUrl');
    const trustedFormToken = document.getElementsByName('xxTrustedFormToken');
    const trustedFormPingUrl = document.getElementsByName('xxTrustedFormPingUrl');
    // Get all <p> elements within the div
    const tcpa = {}
    const trusted_form = {}

    if (disclosureDiv) {
      const paragraphs = disclosureDiv.getElementsByTagName('p');
      // Iterate through the <p> elements and extract their text content
      const textArray = [];
      for (let i = 0; i < paragraphs.length; i++) {
        textArray.push(paragraphs[i].textContent);
      }
      // Join the extracted text content (if needed)
      tcpa.text = textArray.join('\n');
    }

    if (trustedFormCertUrl?.length > 0) {
      trusted_form.cert_url = trustedFormCertUrl?.[0].value;
    }
    if (trustedFormToken?.length > 0) {
      trusted_form.token = trustedFormToken?.[0].value;
    }
    if (trustedFormPingUrl?.length > 0) {
      trusted_form.ping_url = trustedFormPingUrl?.[0].value;
    }

    let newState = {
      ...initialCurrentState,
      jornaya_lead_id: leadid_token.value,
      tcpa: tcpa,
      trusted_form: trusted_form,
      page_groups: initialCurrentState?.page_groups?.map((pg) => ({
        ...pg,
        pages: pg?.pages?.map((p) => ({
          ...p,
          question_groups: p?.question_groups?.map((qg) => ({
            ...qg,
            questions: qg?.questions?.map((q) => ({
              ...q,
              prefilled: q?.prefilled || !!q?.value
            }))
          }))
        }))
      }))
    }

    const payload = {
      section_name: submit_type,
      session_id: session_id,
      quote_form_visit_id: quote_form_visit?.id,
      data: newState
    }
    const { next_page_group_index, next_page_index } = get_next_indexes(newState, answers, (page_group_current_index || 0), (page_current_index || 0) + 1, init_prefilled, review)
    const additional_info = {
      page_current_index: next_page_index,
      page_group_current_index: next_page_group_index,
      review: review,
      preview: preview,
      quote_funnel_id: quote_funnel_id,
      session_id: session_id
    }
    dispatch(updateQuoteFunnelQuestionsWithAnswers(payload, additional_info, history));
  }

  const init_prefilled = useMemo(() => {
    return quote_funnel_questions?.init_prefilled || []
  }, [quote_funnel_questions])

  useEffect(() => {
    if (quote_funnel_questions && !handledInitialPage && !parseInt(query.get('page_current_index'))) {
      setHandledInitialPage(true);
      const { next_page_group_index, next_page_index } = get_next_indexes(
        quote_funnel_questions,
        answers,
        (page_group_current_index || 0),
        0,
        init_prefilled, review);
      if (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_CLIENT_META_IN_QUOTE_FORMS)){
        history.push(`/forms/session/${session_id}?page_group_current_index=${next_page_group_index}&page_current_index=${next_page_index}&review=${review}&preview=${preview}&meta=${meta}`)
      } else {
        history.push(`/forms/session/${session_id}?page_group_current_index=${next_page_group_index}&page_current_index=${next_page_index}&review=${review}&preview=${preview}`)
      }
    }
  }, [quote_funnel_questions])

  return (
    <>
      <Container>
        <Row>
          <Col md={10} className="mx-auto">
            <div style={{ padding: '20px' }}>
              <PageGroup
                quote_funnel_questions_loading={quote_funnel_questions_loading}
                page_group={initialCurrentState?.page_groups[page_group_current_index] || []}
                answers={answers}
                onChangeEvent={(value) => handleOnChangeEvent(value, page_group_current_index)}
                nextPageGroup={handleCurrentPageGroup}
                isLastPageGroup={initialCurrentState?.page_groups?.length === page_group_current_index + 1}
                onPageChange={(submit_type, page_current_index) => saveDetails(submit_type, page_group_current_index, page_current_index)}
                review={review}
                tcpa_disclaimer_text={tcpa_disclaimer_text}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
}

QuoteFunnelAnswer.propTypes = {
  quote_funnel_questions: PropTypes.object,
  quote_funnel_questions_loading: PropTypes.bool,
  quote_form_visit: PropTypes.object
};


export default QuoteFunnelAnswer;